import './footerActionBar.scss';

import { memo, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { Skeleton } from '@eliberty/design-system-react';
import { addDays } from 'date-fns';

import { DECIMAL_DISPLAY, ErrorMessage, FormattedCurrency } from '~atoms';
import { SELECTOR_MODE } from '~constants';
import { getLocale } from '~helpers';
import { DateSummary, PriceDetailsModal, PromotionAlerts } from '~molecules';
import {
  Maybe,
  OrderItem,
  Product,
  ProductCategoryValidity,
  Promotion,
  Simulation,
} from '~types/appsync.types';
import { Participant } from '~types/participant';

interface FooterActionBarProps {
  /** Final price after applying all reductions and promotions */
  finalPrice?: number;
  /** If the price should be displayed */
  showPrice?: boolean;
  /** The price's currency (optional) */
  currency?: string;
  /** The next step button to display */
  nextStepButton: ReactNode;
  /** If simulation has some errors (unable to calculate price, api errors, etc) */
  isSimulationOnError?: boolean;
  /** Base price calculated before applying reductions & promotions, if any*/
  basePrice?: number;
  /** List of promotions */
  promotions?: Promotion[];
  /** List of participants */
  participants?: Participant[];
  /** Current simulation */
  simulation?: Simulation;
  /** Order items returned by the simulation */
  orderItems?: OrderItem[];
  /** The start of the selected range */
  selectedProduct?: Maybe<Product>;
  /** Selected validity category */
  validityCategory?: ProductCategoryValidity;
  /** Selected date */
  date?: Date;
  /** Mode of the participant selector */
  participantMode: SELECTOR_MODE;
  /** If the simulation is loading */
  isLoading?: boolean;
  /** Control if promotion alerts should be rendered inline above the footer */
  renderInlinePromotionAlerts?: boolean;
  /** Whether the selected date has a dynamic price discount */
  hasDynamicDiscount?: boolean;
  /** Optional helper displayed below the price area */
  priceHelper?: ReactNode;
}

/**
 * This component shows a formatted price and a button that is clickable only when the price is defined.
 *
 * If price is provided, it is formatted and displayed. If price is not provided, a placeholder ('--') is displayed.
 */
const FooterActionBar = ({
  finalPrice,
  showPrice = true,
  currency,
  nextStepButton,
  isSimulationOnError = false,
  basePrice,
  promotions,
  participants,
  simulation,
  orderItems,
  selectedProduct,
  validityCategory,
  date,
  participantMode,
  isLoading = false,
  renderInlinePromotionAlerts = true,
  hasDynamicDiscount = false,
  priceHelper,
}: FooterActionBarProps) => {
  const displayCalendar = selectedProduct?.calendarDisplay;
  const locale = getLocale();
  const computedEndDate =
    date && validityCategory?.value && validityCategory.value > 1
      ? addDays(date, validityCategory.value - 1)
      : undefined;

  const hasPromotions = !!promotions && promotions.length > 0;
  const shouldRenderInlinePromotions = renderInlinePromotionAlerts && hasPromotions;

  return (
    <div className={`FooterActionBar ${showPrice === false ? 'FooterActionBar-right' : ''}`}>
      {!isSimulationOnError && showPrice && (
        <div className='FooterActionBar_priceArea'>
          {shouldRenderInlinePromotions && (
            <PromotionAlerts
              promotions={promotions}
              className='FooterActionBar_promotions'
              showDescriptions={false}
              hasDynamicDiscount={hasDynamicDiscount}
            />
          )}
          <output className='FooterActionBar_priceSummary' aria-live='polite'>
            <h2 id='priceSummaryTitle' className='sr-only'>
              <FormattedMessage id='priceSummary' defaultMessage='Price summary' />
            </h2>
            <div>
              {isLoading && <Skeleton data-testid='Skeleton-footer' width='70px' height='24px' />}
              {!isLoading &&
                (finalPrice !== undefined && currency ? (
                  <dl className='FooterActionBar_totalPriceSummaryList'>
                    <div className='FooterActionBar_totalPriceSummaryPrices'>
                      <div>
                        <dt className='sr-only'>
                          <FormattedMessage id='finalPrice' defaultMessage='Final price' />
                        </dt>
                        <dd className='FooterActionBar_priceSummaryFinal' data-testid='finalPrice'>
                          <PriceDetailsModal
                            participants={participants}
                            availableOptions={simulation?.availableOptions}
                            orderItems={orderItems}
                            promotions={promotions}
                            currency={currency}
                            finalPrice={finalPrice}
                            basePrice={basePrice}
                            productName={selectedProduct?.name}
                            participantMode={participantMode}
                            triggerContent={
                              <FormattedCurrency
                                value={finalPrice}
                                currency={currency}
                                decimalDisplay={DECIMAL_DISPLAY.ALWAYS}
                              />
                            }
                          />
                        </dd>
                      </div>
                      {basePrice !== undefined && basePrice !== finalPrice && (
                        <div>
                          <dt className='sr-only'>
                            <FormattedMessage id='initialPrice' defaultMessage='Initial price' />
                          </dt>
                          <dd
                            className='FooterActionBar_priceSummaryOriginal'
                            data-testid='basePrice'
                          >
                            <del>
                              <FormattedCurrency
                                value={basePrice}
                                currency={currency}
                                decimalDisplay={DECIMAL_DISPLAY.ALWAYS}
                              />
                            </del>
                          </dd>
                        </div>
                      )}
                    </div>
                    {priceHelper && (
                      <div className='FooterActionBar_priceHelper'>{priceHelper}</div>
                    )}
                  </dl>
                ) : (
                  <span
                    className='FooterActionBar_priceSummaryFinal FooterActionBar_priceSummaryFinal-noPrice'
                    data-testid='finalPrice'
                  >
                    {'--'}
                  </span>
                ))}
            </div>
          </output>
        </div>
      )}
      {isSimulationOnError && (
        <ErrorMessage
          messageId='unableToCalculatePrice'
          defaultMessage='Unable to calculate a price. Please, try again later.'
        />
      )}
      {!isSimulationOnError && showPrice === false && (
        <div className='FooterActionBar_summary'>
          {validityCategory?.name && (
            <div className='FooterActionBar_line'>
              <span className='FooterActionBar_lineTitle'>
                <FormattedMessage id='duration' defaultMessage='Duration' />
              </span>
              <span className='FooterActionBar_lineValue' data-testid='summary-validity'>
                {validityCategory.name}
              </span>
            </div>
          )}

          {selectedProduct && (date || (!date && !displayCalendar)) && (
            <div className='FooterActionBar_line'>
              <span className='FooterActionBar_lineTitle'>
                <FormattedMessage id='date' defaultMessage='Date' />
              </span>
              <span className='FooterActionBar_lineValue' data-testid='summary-date'>
                <DateSummary
                  displayCalendar={!!displayCalendar}
                  startDate={date}
                  endDate={computedEndDate}
                  locale={locale}
                />
              </span>
            </div>
          )}
          {participants && participants.length > 0 && (
            <div className='FooterActionBar_line'>
              <span className='FooterActionBar_lineTitle'>
                <FormattedMessage id='participants' defaultMessage='Participants' />
              </span>
              <span className='FooterActionBar_lineValue'>{participants.length}</span>
            </div>
          )}
        </div>
      )}
      {nextStepButton}
    </div>
  );
};

export default memo(FooterActionBar);
